import { createApi } from '@reduxjs/toolkit/query/react';
import { buildBaseQuery } from 'app/api';
import { Global } from 'constants/index';
import { Calibration } from 'app/types/calibration';
import { LoggerSearch, QuickSearch } from 'app/types/search';
import { CodeValidation, LoggerUsername } from 'app/types/validation';
import { toQueryString } from 'utils/url';
import type {
  CreateLogger,
  LoggerAddressSearch,
  LoggerAreaSearch,
  CreateLoggerProject,
  Logger,
  LoggerCountries,
  LoggerLink,
  LoggerList,
  LoggerOverviewRequest,
  LoggerPublicOverview,
  LoggerWorkNameSearch,
  ToolOption,
  UpdateLoggerProject
} from './types';
import { AreaOption, StreetOption, toAreaOptions, toStreetOptions } from 'app/services/addresses/types';

const name = 'loggerApi';

const tags = {
  LIST: 'LoggerList',
  ENTRY: 'Logger'
};

export const loggersApi = createApi({
  reducerPath: name,
  baseQuery: buildBaseQuery('api/loggers'),
  tagTypes: Object.values(tags),
  endpoints: (builder) => ({
    getLoggers: builder.query<LoggerList, void>({
      query: () => '',
      providesTags: [ tags.LIST ],
    }),
    createLogger: builder.mutation<number, CreateLogger>({
      query: (values) => ({
        url: '',
        method: 'POST',
        body: values
      }),
      invalidatesTags: [ tags.LIST ]
    }),
    isLoggerCodeAvailable: builder.query<boolean, CodeValidation>({
      query: (codeValidation) => {
        let query = `/validate/code?code=${codeValidation.code}`;
        if (codeValidation.id) query += `&loggerId=${codeValidation.id}`;
        return query;
      },
      transformResponse: (response: any) => response.Result,
    }),
    isLoggerBarcodeAvailable: builder.query<boolean, CodeValidation>({
      query: (barcodeValidation) => {
        let query = `/validate/barcode?code=${barcodeValidation.code}`;
        if (barcodeValidation.id) query += `&loggerId=${barcodeValidation.id}`;
        return query;
      },
      transformResponse: (response: any) => response.Result,
    }),
    generateLoggerQrCodes: builder.mutation<string[], number>({
      query: (count) => ({
        url: '/qrCode/generate',
        method: 'POST',
        body: { count },
      }),
      transformResponse: (response: any) => response.Codes,
    }),
    updateLogger: builder.mutation<boolean, Logger>({
      query: (value) => ({
        url: '',
        method: 'PUT',
        body: value
      }),
      invalidatesTags: [ tags.ENTRY, tags.LIST ]
    }),
    getLoggerById: builder.query<Logger, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'GET',
      }),
      providesTags: [ tags.ENTRY ],
      transformResponse: (response: any) => response.Logger
    }),
    addLoggerCalibration: builder.mutation<boolean, Calibration>({
      query: (value) => ({
        url: '/calibration',
        method: 'POST',
        body: value
      }),
      invalidatesTags: [ tags.ENTRY, tags.LIST ]
    }),
    searchLogger: builder.query<LoggerSearch[], string>({
      query: (search) => `?search=${search}`,
      transformResponse: (response: any) => response.LoggerList.map((logger: any) => ({
        label: logger.Code,
        value: logger.Code,
        UnitId: logger.UnitId
      }))
    }),
    addLoggerLink: builder.mutation<number, LoggerLink>({
      query: (value) => ({
        url: '/link',
        method: 'POST',
        body: value
      }),
      invalidatesTags: [ tags.ENTRY ]
    }),
    isUserNameAvailable: builder.query<boolean, LoggerUsername>({
      query: (username) => `/validate/username?${toQueryString(username)}`,
      transformResponse: (response: any) => response.Result,
    }),
    // Anonymous (works without a session): the public logger overview behind the QR code on a v3
    // logger. The 4-digit verification code shown on the logger screen travels in a header, checked by
    // the backend's VerifyLoggerCode filter before each of these.
    getLoggerOverview: builder.query<LoggerPublicOverview, LoggerOverviewRequest>({
      query: ({ barcode, code }) => ({
        url: `/overview/${encodeURIComponent(barcode)}`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      })
    }),
    continueLoggerProject: builder.mutation<void, LoggerOverviewRequest>({
      query: ({ barcode, code }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/continue`,
        method: 'POST',
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      })
    }),
    endLoggerWork: builder.mutation<void, LoggerOverviewRequest>({
      query: ({ barcode, code }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/endwork`,
        method: 'POST',
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      })
    }),
    getLoggerProjectTools: builder.query<ToolOption[], LoggerOverviewRequest>({
      query: ({ barcode, code }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/tools`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      })
    }),
    getLoggerWorkNames: builder.query<QuickSearch[], LoggerWorkNameSearch>({
      query: ({ barcode, code, countryId, search }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/worknames?${toQueryString({ countryId, search })}`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      }),
      transformResponse: (response: any) =>
        response.WorkNameList.map((workName: string) => ({ label: workName, value: workName })),
    }),
    getLoggerAreas: builder.query<AreaOption[], LoggerAreaSearch>({
      query: ({ barcode, code, search, countryId }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/areas?${toQueryString({ search, countryId })}`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      }),
      transformResponse: (response: any) => toAreaOptions(response.AreaList),
    }),
    getLoggerAddresses: builder.query<StreetOption[], LoggerAddressSearch>({
      query: ({ barcode, code, search, countryId, area, areaFilter }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/addresses?${toQueryString({ search, countryId, area, areaFilter })}`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      }),
      transformResponse: (response: any) => toStreetOptions(response.AddressList),
    }),
    getLoggerCountries: builder.query<LoggerCountries, LoggerOverviewRequest>({
      query: ({ barcode, code }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/countries`,
        headers: { [Global.LOGGER_CODE_HEADER]: code }
      }),
    }),
    createLoggerProject: builder.mutation<{ Id: number }, CreateLoggerProject>({
      query: ({ barcode, code, project, submissionKey }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/workplace`,
        method: 'POST',
        headers: { [Global.LOGGER_CODE_HEADER]: code, [Global.LOGGER_SUBMISSION_HEADER]: submissionKey },
        body: project
      })
    }),
    updateLoggerProject: builder.mutation<void, UpdateLoggerProject>({
      query: ({ barcode, code, project }) => ({
        url: `/overview/${encodeURIComponent(barcode)}/project`,
        method: 'PUT',
        headers: { [Global.LOGGER_CODE_HEADER]: code },
        body: project
      })
    }),
  }),
  refetchOnMountOrArgChange: true,
});

export const {
  useGetLoggersQuery,
  useCreateLoggerMutation,
  useLazyIsLoggerCodeAvailableQuery,
  useLazyIsLoggerBarcodeAvailableQuery,
  useGenerateLoggerQrCodesMutation,
  useUpdateLoggerMutation,
  useGetLoggerByIdQuery,
  useAddLoggerCalibrationMutation,
  useLazySearchLoggerQuery,
  useAddLoggerLinkMutation,
  useLazyIsUserNameAvailableQuery,
  useLazyGetLoggerOverviewQuery,
  useLazyGetLoggerProjectToolsQuery,
  useContinueLoggerProjectMutation,
  useEndLoggerWorkMutation,
  useCreateLoggerProjectMutation,
  useLazyGetLoggerWorkNamesQuery,
  useLazyGetLoggerAreasQuery,
  useLazyGetLoggerAddressesQuery,
  useGetLoggerCountriesQuery,
  useUpdateLoggerProjectMutation
} = loggersApi;